import React, { useState, useEffect, useRef, useLayoutEffect, lazy, Suspense } from 'react';
import { motion, AnimatePresence, LazyMotion, domMax, MotionConfig } from 'motion/react';
import useSWR, { mutate } from 'swr';
import { LucideIcon, Menu, X, LogIn, Settings, LogOut, Plus, Trash2, ChevronRight, CheckCircle2, Phone, Mail, MapPin, Bed, Shirt, Bath, Utensils, Car, ShoppingCart, Stethoscope, Moon, Clock, Brain, ShieldCheck, Building2, Globe, ArrowRight, Star, Briefcase, Upload, Accessibility, Facebook } from 'lucide-react';
import { Content, Service, News } from './types';

const fetcher = (url: string) => fetch(url).then(res => res.json());

const getIcon = (iconName: string) => {
  const icons: Record<string, any> = {
    bed: Bed,
    checkroom: Shirt,
    bathtub: Bath,
    restaurant: Utensils,
    directions_car: Car,
    shopping_cart: ShoppingCart,
    medical_services: Stethoscope,
    nightlight: Moon,
    timer: Clock,
    psychology: Brain,
  };
  return icons[iconName] || CheckCircle2;
};

const ZoneMap = ({ isMobile }: { isMobile: boolean }) => (
  <div className="relative w-full aspect-[16/9] bg-slate-50 rounded-3xl overflow-hidden border border-slate-100 shadow-inner group">
    <svg viewBox="0 0 800 450" className="w-full h-full" role="img" aria-labelledby="map-title map-desc">
      <title id="map-title">Carte des zones d'intervention Domo74</title>
      <desc id="map-desc">Carte montrant les zones d'intervention de Domo74 en Haute-Savoie, incluant le Genevois et le Chablais.</desc>
      {/* Background/Region Labels */}
      <text x="150" y="350" className="fill-slate-200 font-black text-4xl uppercase tracking-[0.2em] pointer-events-none">Genevois</text>
      <text x="550" y="250" className="fill-slate-200 font-black text-4xl uppercase tracking-[0.2em] pointer-events-none">Chablais</text>
      
      {/* Lac Léman (Lake Geneva) - Simplified Crescent Shape */}
      <path 
        d="M 150 150 Q 400 50 650 150 Q 400 120 150 150" 
        className="fill-blue-100 stroke-blue-200 stroke-2"
      />
      <text x="380" y="100" className="fill-blue-300 font-bold text-[10px] uppercase tracking-widest italic">Lac Léman</text>

      {/* Intervention Zones (Simplified polygons) */}
      <motion.path 
        d="M 50 200 L 250 200 L 300 400 L 100 400 Z" 
        className="fill-primary/5 stroke-primary/20 stroke-1"
        animate={!isMobile ? { 
          fillOpacity: [0.05, 0.15, 0.05],
          strokeOpacity: [0.2, 0.5, 0.2]
        } : false}
        transition={!isMobile ? { duration: 4, repeat: Infinity, ease: "easeInOut" } : undefined}
      />
      <motion.path 
        d="M 400 150 L 750 150 L 700 350 L 450 350 Z" 
        className="fill-primary/5 stroke-primary/20 stroke-1"
        animate={!isMobile ? { 
          fillOpacity: [0.05, 0.15, 0.05],
          strokeOpacity: [0.2, 0.5, 0.2]
        } : false}
        transition={!isMobile ? { duration: 4, repeat: Infinity, ease: "easeInOut", delay: 1 } : undefined}
      />

      {/* Office 1: Genevois (Annemasse) */}
      <g className="cursor-pointer group/office1">
        <circle cx="180" cy="180" r="8" className="fill-secondary animate-pulse" />
        <circle cx="180" cy="180" r="4" className="fill-white" />
        <foreignObject x="195" y="165" width="150" height="50">
          <div className="bg-white p-2 rounded-lg shadow-lg border border-slate-100 opacity-0 group-hover/office1:opacity-100 transition-opacity">
            <p className="text-[10px] font-black text-slate-900">Bureau du Genevois</p>
            <p className="text-[8px] text-slate-700">Annemasse</p>
          </div>
        </foreignObject>
      </g>

      {/* Office 2: Chablais (Veigy-Foncenex) */}
      <g className="cursor-pointer group/office2">
        <circle cx="550" cy="160" r="8" className="fill-secondary animate-pulse" />
        <circle cx="550" cy="160" r="4" className="fill-white" />
        <foreignObject x="565" y="145" width="150" height="50">
          <div className="bg-white p-2 rounded-lg shadow-lg border border-slate-100 opacity-0 group-hover/office2:opacity-100 transition-opacity">
            <p className="text-[10px] font-black text-slate-900">Bureau du Chablais</p>
            <p className="text-[8px] text-slate-700">Veigy-Foncenex</p>
          </div>
        </foreignObject>
      </g>

      {/* Legend */}
      <g transform="translate(30, 30)">
        <rect width="180" height="60" rx="10" className="fill-white/80 backdrop-blur-sm stroke-slate-100" />
        <circle cx="20" cy="20" r="4" className="fill-secondary" />
        <text x="35" y="24" className="fill-slate-600 text-[10px] font-bold">Bureaux Domo74</text>
        <rect x="14" y="36" width="12" height="12" className="fill-secondary/10 stroke-secondary/30" />
        <text x="35" y="46" className="fill-slate-600 text-[10px] font-bold">Zone d'intervention</text>
      </g>
    </svg>
    
    <div className="absolute bottom-8 left-1/2 -translate-x-1/2 bg-white/90 backdrop-blur-md px-6 py-3 rounded-full shadow-xl border border-white flex items-center gap-4">
      <div className="flex items-center gap-2">
        <div className="size-2 bg-secondary rounded-full animate-ping" />
        <span className="text-xs font-black uppercase tracking-widest">Disponibilité Immédiate</span>
      </div>
      <div className="w-px h-4 bg-slate-200" />
      <span className="text-[10px] text-slate-700 font-medium">Intervention sous 24h à 48h</span>
    </div>
  </div>
);

// Lazy load complex components
const LegalModal = lazy(() => import('./components/LegalModal'));
const QuoteForm = lazy(() => import('./components/QuoteForm'));
const RecruitmentForm = lazy(() => import('./components/RecruitmentForm'));
import { ScrollAnimate } from './components/ScrollAnimate';

// Device-aware prefetching to balance LCP and bundle size
if (typeof window !== 'undefined') {
  const isDesktop = window.innerWidth >= 1024;
  if (isDesktop) {
    // Eagerly prefetch QuoteForm on desktop as it's part of the hero LCP region
    const prefetchQuote = () => import('./components/QuoteForm');
    // Use requestIdleCallback if available, otherwise setTimeout
    if ('requestIdleCallback' in window) {
      (window as any).requestIdleCallback(prefetchQuote);
    } else {
      setTimeout(prefetchQuote, 100);
    }
  }
}

// Use useLayoutEffect on client to avoid layout thrashing during initial render
const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

export default function App() {
  const [isReady, setIsReady] = useState(false);
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth < 768;
    }
    return false;
  });
  const [isAdmin, setIsAdmin] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [activeLegal, setActiveLegal] = useState<string | null>(null);

  useIsomorphicLayoutEffect(() => {
    // Use matchMedia for much better performance than 'resize' event + innerWidth
    const mediaQuery = window.matchMedia('(max-width: 767px)');
    const handleMediaChange = (e: MediaQueryListEvent | MediaQueryList) => {
      setIsMobile(e.matches);
    };

    // Initial check
    handleMediaChange(mediaQuery);

    // Set ready state in the next frame to avoid blocking the main thread
    const timer = requestAnimationFrame(() => {
      setIsReady(true);
    });

    mediaQuery.addEventListener('change', handleMediaChange);
    return () => {
      cancelAnimationFrame(timer);
      mediaQuery.removeEventListener('change', handleMediaChange);
    };
  }, []);
  const swrOptions = {
    revalidateOnFocus: false,
    revalidateIfStale: false,
    revalidateOnReconnect: false,
    dedupingInterval: 3600000, // 1 hour cache
    focusThrottleInterval: 3600000,
  };

  const fallbackContent: Content = {
    hero_title: "Aide à domicile en Haute-Savoie",
    hero_subtitle: "Au service des personnes âgées, dépendantes ou en situation de handicap sur toute la Haute-Savoie. Une présence forte sur Annemasse, le Genevois et le Chablais.",
    hero_image: "/1_11zon.webp",
    about_title: "DOMO74 : Votre Service Autonomie en Haute-Savoie",
    about_text: "DOMO74 est un service autonomie dédié à l’accompagnement quotidien des personnes âgées, en perte d’autonomie ou en situation de handicap.",
    financing_text: "Nos prestations peuvent être prises en charge par plusieurs dispositifs : le Conseil Départemental de Haute-Savoie (APA, PCH)...",
    contact_email: "contact@domo74.fr",
    contact_phone: "04 56 30 74 06",
    contact_address: "74000 Annecy, Haute-Savoie",
    contact_address_annemasse: "11 avenue Jules Ferry, 74100 Annemasse",
    contact_address_veigy: "55 rue des Fleurs, 74140 Veigy-Foncenex",
    about_image: "/2_11zon.webp",
    contact_image: "https://images.unsplash.com/photo-1516733725897-1aa73b87c8e8?auto=format&fit=crop&q=80&w=800&h=400",
    logo: "/logodomo.webp",
    quote_subtitle: "Une réponse personnalisée sous 48h pour votre accompagnement à domicile.",
    agency_code: "do74",
    google_verification_code: "",
    google_ads_code: "AW-845496361"
  };

  const fallbackServices: Service[] = [
    {
      id: 1,
      title: "Aide au lever et au coucher",
      description: "Nos auxiliaires de vie veillent à ce que chaque moment de transition dans la journée se fasse en toute sécurité et avec douceur.",
      icon: "bed",
      image: "https://images.unsplash.com/photo-1576765608535-5f04d1e3f289?auto=format&fit=crop&q=80&w=800&h=600"
    },
    {
      id: 2,
      title: "Aide à l’habillage",
      description: "Accompagnement dans le choix des vêtements et lors de l'habillage, en veillant à préserver votre confort.",
      icon: "checkroom",
      image: "https://images.unsplash.com/photo-1581578731522-745d05ad9a2d?auto=format&fit=crop&q=80&w=800&h=600"
    },
    {
      id: 3,
      title: "Aide à la toilette et à la douche",
      description: "Aide à la toilette quotidienne en toute intimité, avec respect et bienveillance, au lavabo ou à la douche.",
      icon: "bathtub",
      image: "https://images.unsplash.com/photo-1576765608866-5b5104e1428c?auto=format&fit=crop&q=80&w=800&h=600"
    },
    {
      id: 4,
      title: "Aide à la préparation des repas",
      description: "Assistance dans la préparation, le réchauffage ou la prise des repas, attentive à vos goûts et à votre équilibre.",
      icon: "restaurant",
      image: "https://images.unsplash.com/photo-1581578731522-745d05ad9a2d?auto=format&fit=crop&q=80&w=800&h=600"
    },
    {
      id: 5,
      title: "Accompagnement sorties",
      description: "Accompagnement pédestre ou véhiculé pour vos rendez-vous médicaux, courses, activités sociales ou balades.",
      icon: "directions_car",
      image: "https://images.unsplash.com/photo-1516733725897-1aa73b87c8e8?auto=format&fit=crop&q=80&w=800&h=600"
    },
    {
      id: 6,
      title: "Courses hebdomadaires",
      description: "Aide pour réaliser vos courses en tenant compte de vos envies et de votre budget, ou réalisation à votre place.",
      icon: "shopping_cart",
      image: "https://images.unsplash.com/photo-1542838132-92c53300491e?auto=format&fit=crop&q=80&w=800&h=600"
    },
    {
      id: 7,
      title: "Aspiration endotrachéale",
      description: "Intervention spécialisée auprès de personnes trachéotomisées dans le strict respect des protocoles d'hygiène.",
      icon: "medical_services",
      image: "https://images.unsplash.com/photo-1581056771107-24ca5f033842?auto=format&fit=crop&q=80&w=800&h=600"
    },
    {
      id: 8,
      title: "Garde jour et nuit",
      description: "Présence bienveillante 24h/24 pour sécuriser, accompagner et soulager les aidants familiaux.",
      icon: "nightlight",
      image: "https://images.unsplash.com/photo-1516733725897-1aa73b87c8e8?auto=format&fit=crop&q=80&w=800&h=400"
    },
    {
      id: 9,
      title: "Heures d'attention",
      description: "Présence active sur des créneaux choisis pour offrir écoute, compagnie et soutien à domicile.",
      icon: "timer",
      image: "https://images.unsplash.com/photo-1581578731522-745d05ad9a2d?auto=format&fit=crop&q=80&w=800&h=600"
    },
    {
      id: 10,
      title: "Ateliers mémoire",
      description: "Jeux de stimulation et activités adaptées pour préserver la mémoire et maintenir le lien social.",
      icon: "psychology",
      image: "https://images.unsplash.com/photo-1529156069898-49953e39b3ac?auto=format&fit=crop&q=80&w=800&h=600"
    }
  ];

  const { data: content = fallbackContent, mutate: mutateContent } = useSWR<Content>('/api/content', fetcher, { ...swrOptions, fallbackData: fallbackContent });
  const { data: services = fallbackServices, mutate: mutateServices } = useSWR<Service[]>('/api/services', fetcher, { ...swrOptions, fallbackData: fallbackServices });
  const { data: news = [], mutate: mutateNews } = useSWR<News[]>('/api/news', fetcher, { ...swrOptions, fallbackData: [] });
  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const [show2FA, setShow2FA] = useState(false);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{ id: number, type: 'news' | 'service', title: string } | null>(null);
  const [showAddService, setShowAddService] = useState(false);
  const [newService, setNewService] = useState({ title: '', description: '', icon: 'bed' });
  const [showAddNews, setShowAddNews] = useState(false);
  const [newNews, setNewNews] = useState({ title: '', content: '', category: 'Actualités', image: '' });

  useEffect(() => {
    const adminToken = localStorage.getItem('admin_token');
    if (adminToken) setIsAdmin(true);
  }, []);

  useEffect(() => {
    if (content) {
      // Google Site Verification
      if (content.google_verification_code) {
        let meta = document.querySelector('meta[name="google-site-verification"]');
        if (!meta) {
          meta = document.createElement('meta');
          meta.setAttribute('name', 'google-site-verification');
          document.head.appendChild(meta);
        }
        meta.setAttribute('content', content.google_verification_code);
      }

      // Google Ads / Analytics
      if (content.google_ads_code) {
        const existingScript = document.querySelector('script[data-google-tag]');
        if (existingScript) existingScript.remove();

        if (content.google_ads_code.includes('<script')) {
          // Full snippet
          const temp = document.createElement('div');
          temp.innerHTML = content.google_ads_code;
          const scripts = temp.querySelectorAll('script');
          scripts.forEach(s => {
            const newScript = document.createElement('script');
            Array.from(s.attributes).forEach(attr => newScript.setAttribute(attr.name, attr.value));
            newScript.innerHTML = s.innerHTML;
            newScript.setAttribute('data-google-tag', 'true');
            newScript.setAttribute('defer', '');
            document.body.appendChild(newScript);
          });
        } else if (content.google_ads_code.trim()) {
          // Just ID
          const id = content.google_ads_code.trim();
          const script1 = document.createElement('script');
          script1.defer = true;
          script1.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
          script1.setAttribute('data-google-tag', 'true');
          document.body.appendChild(script1);

          const script2 = document.createElement('script');
          script2.innerHTML = `
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', '${id}');
          `;
          script2.setAttribute('data-google-tag', 'true');
          script2.setAttribute('defer', '');
          document.body.appendChild(script2);
        }
      }
    }
  }, [content]);

  const fetchData = () => {
    mutateContent();
    mutateServices();
    mutateNews();
  };

  const handleOpenExtranet = () => {
    const agencyCode = content?.agency_code || 'do74';
    window.open(`https://extranet.ximi.xelya.io/Ximi2/${agencyCode}/`, '_blank');
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const res = await fetch('/api/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loginForm)
      });
      const data = await res.json();
      if (data.requires2FA) {
        setShow2FA(true);
      } else if (data.success) {
        setIsAdmin(true);
        setShowLogin(false);
        localStorage.setItem('admin_token', data.token);
        fetchData();
      } else {
        alert(data.message);
      }
    } catch (err) {
      alert("Erreur de connexion");
    }
  };

  const handleVerify2FA = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const res = await fetch('/api/verify-2fa', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: loginForm.username, code: twoFactorCode })
      });
      const data = await res.json();
      if (data.success) {
        setIsAdmin(true);
        setShowLogin(false);
        setShow2FA(false);
        setTwoFactorCode('');
        localStorage.setItem('admin_token', data.token);
        fetchData();
      } else {
        alert(data.message);
      }
    } catch (err) {
      alert("Erreur de vérification");
    }
  };

  const handleLogout = () => {
    setIsAdmin(false);
    localStorage.removeItem('admin_token');
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    const endpoint = deleteConfirm.type === 'news' ? `/api/admin/delete-news/${deleteConfirm.id}` : `/api/admin/delete-service/${deleteConfirm.id}`;
    await fetch(endpoint, { method: 'DELETE' });
    if (deleteConfirm.type === 'news') mutateNews();
    else mutateServices();
    setDeleteConfirm(null);
  };

  const handleAddService = async (e: React.FormEvent) => {
    e.preventDefault();
    await fetch('/api/admin/add-service', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...newService, image: '' })
    });
    mutateServices();
    setShowAddService(false);
    setNewService({ title: '', description: '', icon: 'bed' });
  };

  const handleAddNews = async (e: React.FormEvent) => {
    e.preventDefault();
    await fetch('/api/admin/add-news', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(newNews)
    });
    mutateNews();
    setShowAddNews(false);
    setNewNews({ title: '', content: '', category: 'Actualités', image: '' });
  };

  const updateContent = async (id: string, value: string) => {
    await fetch('/api/admin/update-content', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id, value })
    });
    mutateContent();
  };

  const handleImageUpload = async (file: File, callback: (base64: string) => void) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      callback(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  if (!content) return <div className="flex items-center justify-center h-screen">Chargement...</div>;

  return (
    <LazyMotion features={domMax}>
      <MotionConfig transition={isMobile ? { duration: 0 } : undefined}>
        <div className="min-h-screen bg-background-light">
        {/* Navigation */}
      <div className="fixed top-0 left-0 right-0 z-50">
        {/* Top Bar */}
        <div className="bg-slate-900 text-white py-2 px-6">
          <div className="max-w-7xl mx-auto flex justify-between items-center text-[10px] font-bold uppercase tracking-widest">
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Phone className="size-3 text-primary" />
                <span>{content.contact_phone}</span>
              </div>
              <div className="hidden sm:flex items-center gap-2 text-slate-600">
                <div className="size-1 bg-slate-700 rounded-full" />
                <span>Haute-Savoie & Genève</span>
              </div>
            </div>
            
            <div className="flex items-center gap-6">
              {isAdmin ? (
                <button onClick={handleLogout} className="flex items-center gap-2 text-red-400 hover:text-red-300" aria-label="Se déconnecter de l'administration">
                  <ShieldCheck className="size-3" /> Admin
                </button>
              ) : (
                <button onClick={() => setShowLogin(true)} className="text-slate-600 hover:text-primary transition-colors" aria-label="Ouvrir la connexion administrateur">
                  <Settings className="size-3" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Main Header */}
        <header className="bg-white/80 backdrop-blur-xl border-b border-slate-100 shadow-sm sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
            <a href="/" className="flex items-center gap-3 group relative">
              <img 
                src="/logodomo.webp" 
                srcSet="/logodomo.webp 150w, /logodomo.webp 80w"
                sizes="(max-width: 1024px) 150px, 80px"
                width="169" 
                height="45" 
                alt="Domo74" 
                loading="eager"
                fetchPriority="high"
                className="header-logo h-10 w-auto object-contain"
              />
              {isAdmin && (
                <label className="absolute inset-0 flex items-center justify-center bg-white/80 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer rounded-2xl z-10" aria-label="Changer le logo">
                  <Plus className="size-5 text-primary" aria-hidden="true" />
                  <input 
                    type="file" 
                    className="hidden" 
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleImageUpload(file, (base64) => updateContent('logo', base64));
                    }}
                  />
                </label>
              )}
            </a>

            <nav className="hidden lg:flex items-center gap-4 xl:gap-6">
              <a href="#" className="text-sm font-semibold text-slate-700 hover:text-primary hover:bg-slate-100/70 px-3 py-1.5 rounded-lg transition-all whitespace-nowrap">Accueil</a>
              <a href="#services" className="text-sm font-semibold text-slate-700 hover:text-primary hover:bg-slate-100/70 px-3 py-1.5 rounded-lg transition-all whitespace-nowrap">Services</a>
              <a href="#about" className="text-sm font-semibold text-slate-700 hover:text-primary hover:bg-slate-100/70 px-3 py-1.5 rounded-lg transition-all whitespace-nowrap">Pourquoi nous</a>
              <a href="#news" className="text-sm font-semibold text-slate-700 hover:text-primary hover:bg-slate-100/70 px-3 py-1.5 rounded-lg transition-all whitespace-nowrap">Actualités</a>
              <a href="#recrutement" className="text-sm font-semibold text-slate-700 hover:text-primary hover:bg-slate-100/70 px-3 py-1.5 rounded-lg transition-all whitespace-nowrap">Recrutement</a>
              <a href="#contact" className="text-sm font-semibold text-slate-700 hover:text-primary hover:bg-slate-100/70 px-3 py-1.5 rounded-lg transition-all whitespace-nowrap">Contact</a>
              
              <button 
                onClick={handleOpenExtranet}
                className="text-sm font-bold text-primary bg-primary/10 hover:bg-primary hover:text-white border border-primary/20 px-4 py-2 rounded-xl transition-all shadow-sm flex items-center gap-2 whitespace-nowrap group ml-2"
                aria-label="Accéder à mon planning extranet"
              >
                <Globe className="size-4 group-hover:rotate-12 transition-transform" />
                <span>Mon Planning</span>
              </button>
            </nav>

            <div className="flex items-center gap-4">
              {isAdmin && (
                <button onClick={handleLogout} className="flex items-center gap-2 px-4 py-2 bg-red-50 text-red-600 rounded-full hover:bg-red-100 transition-colors text-[10px] font-black uppercase tracking-widest border border-red-100 whitespace-nowrap">
                  <LogOut className="size-3" />
                  Admin
                </button>
              )}
              <a href="#devis" className="bg-secondary text-white px-6 py-2.5 rounded-xl font-bold text-sm shadow-md hover:bg-secondary-dark hover:shadow-lg active:scale-95 transition-all hidden sm:flex items-center gap-2 group border-b-2 border-secondary-dark whitespace-nowrap">
                Devis gratuit
                <ArrowRight className="size-4 group-hover:translate-x-1 transition-transform" />
              </a>
              <button 
                onClick={() => setIsMenuOpen(true)}
                className="lg:hidden text-slate-700 p-2 hover:bg-slate-100 rounded-xl transition-colors"
                aria-label="Ouvrir le menu"
              >
                <Menu className="size-6" />
              </button>
            </div>
          </div>
        </header>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMenuOpen && (
            <div className="fixed inset-0 z-[100] lg:hidden">
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setIsMenuOpen(false)}
                className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
              />
              <motion.div 
                initial={{ x: '100%' }}
                animate={{ x: 0 }}
                exit={{ x: '100%' }}
                transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                className="absolute right-0 top-0 bottom-0 w-[300px] bg-white shadow-2xl p-8 flex flex-col gap-8"
              >
                <div className="flex items-center justify-between">
                  <div className="text-xl font-black tracking-tighter text-slate-900">DOMO74</div>
                  <button onClick={() => setIsMenuOpen(false)} className="p-2 hover:bg-slate-100 rounded-xl transition-colors" aria-label="Fermer le menu">
                    <X className="size-6 text-slate-600" />
                  </button>
                </div>
                
                <nav className="flex flex-col gap-6">
                  <a href="#" onClick={() => setIsMenuOpen(false)} className="text-lg font-bold text-slate-900">Accueil</a>
                  <a href="#services" onClick={() => setIsMenuOpen(false)} className="text-lg font-bold text-slate-900">Services</a>
                  <a href="#about" onClick={() => setIsMenuOpen(false)} className="text-lg font-bold text-slate-900">Pourquoi nous</a>
                  <a href="#news" onClick={() => setIsMenuOpen(false)} className="text-lg font-bold text-slate-900">Actualités</a>
                  <a href="#recrutement" onClick={() => setIsMenuOpen(false)} className="text-lg font-bold text-slate-900">Recrutement</a>
                  <a href="#contact" onClick={() => setIsMenuOpen(false)} className="text-lg font-bold text-slate-900">Contact</a>
                  <button 
                    onClick={() => {
                      handleOpenExtranet();
                      setIsMenuOpen(false);
                    }}
                    className="text-lg font-black text-primary flex items-center gap-2"
                  >
                    <Globe className="size-5" />
                    Mon Planning
                  </button>
                </nav>

                <div className="mt-auto pt-8 border-t border-slate-100">
                  <a 
                    href="#devis" 
                    onClick={() => setIsMenuOpen(false)}
                    className="w-full bg-secondary text-white px-6 py-4 rounded-2xl font-black text-center shadow-lg shadow-secondary/20 flex items-center justify-center gap-2"
                  >
                    Devis gratuit
                    <ArrowRight className="size-5" />
                  </a>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>
      </div>

      <main>
        {/* Hero Section */}
        <section id="hero" className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 lg:min-h-[800px] overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(37,99,235,0.05),transparent_50%)]"></div>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,rgba(242,125,38,0.05),transparent_50%)]"></div>
          <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: 'radial-gradient(#000 1px, transparent 1px)', backgroundSize: '24px 24px' }}></div>
          
          <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center relative">
          <motion.div 
            initial={false}
            animate={(!isMobile && isReady) ? { y: 0 } : false}
            className="flex flex-col gap-10"
          >
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-secondary/10 text-secondary text-[10px] font-black uppercase tracking-[0.2em] w-fit border border-secondary/20">
              <CheckCircle2 className="size-3" />
              Autorisé par le Conseil Départemental 74
            </div>
            <div className="space-y-6">
              {isAdmin ? (
                <textarea 
                  className="text-5xl lg:text-7xl font-extrabold leading-[1.1] tracking-tight text-primary bg-white border border-primary/20 p-2 rounded-2xl w-full"
                  value={content.hero_title}
                  onChange={(e) => updateContent('hero_title', e.target.value)}
                />
              ) : (
                <h1 className="text-5xl lg:text-7xl font-extrabold leading-[1.1] tracking-tight text-primary">
                  {content.hero_title}
                </h1>
              )}
              {isAdmin ? (
                <textarea 
                  className="text-xl text-slate-600 bg-white border border-primary/20 p-2 rounded-2xl w-full"
                  value={content.hero_subtitle}
                  onChange={(e) => updateContent('hero_subtitle', e.target.value)}
                />
              ) : (
                <p className="text-xl text-slate-600 max-w-lg leading-relaxed font-medium">
                  {content.hero_subtitle}
                </p>
              )}
            </div>
            <div className="flex flex-wrap gap-6">
              <a href="#contact" className="px-12 py-6 bg-primary text-white rounded-[2rem] font-black uppercase tracking-widest hover:bg-primary/90 hover:scale-105 hover:shadow-[0_25px_50px_-12px_rgba(37,99,235,0.5)] transition-all shadow-2xl shadow-primary/30 flex items-center gap-3 group relative overflow-hidden border-b-4 border-primary-dark">
                <span className="relative z-10">Nous Contacter</span>
                <ChevronRight className="size-6 group-hover:translate-x-1 transition-transform relative z-10" />
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
              </a>
              <button 
                onClick={handleOpenExtranet}
                className="px-12 py-6 bg-white text-slate-900 rounded-[2rem] font-black uppercase tracking-widest hover:bg-slate-50 hover:scale-105 hover:shadow-2xl transition-all border-2 border-slate-100 shadow-xl flex items-center gap-3 group"
                aria-label="Accéder à mon planning extranet (Hero)"
              >
                <Globe className="size-6 text-primary group-hover:rotate-12 transition-transform" />
                Mon Planning
              </button>
            </div>
            <div className="flex items-center gap-6 pt-8 border-t border-slate-100 families-counter">
              <div className="flex flex-col">
                <div className="flex items-center gap-1 text-amber-400 min-h-[12px]">
                  {[1, 2, 3, 4, 5].map(i => <Star key={i} className="size-3 fill-current" aria-hidden="true" />)}
                </div>
                <p className="text-xs text-slate-700 font-bold uppercase tracking-widest mt-1">+500 familles accompagnées</p>
              </div>
            </div>
          </motion.div>
          <motion.div 
            initial={false}
            animate={(!isMobile && isReady) ? { opacity: 1, scale: 1 } : false}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="relative group lg:ml-auto w-full max-w-lg aspect-[4/5]"
          >
            <div className="absolute -inset-10 bg-gradient-to-tr from-primary/20 to-secondary/20 rounded-[3rem] blur-3xl opacity-50 group-hover:opacity-70 transition-opacity"></div>
            <div className="relative rounded-[3rem] overflow-hidden shadow-[0_50px_100px_-20px_rgba(0,0,0,0.25)] animate-pro-frame aspect-[4/5]">
              <img 
                id="hero-img"
                src="/1_11zon.webp" 
                srcSet="/1-mobile.webp 400w, /1_11zon.webp 800w"
                sizes="(max-width: 600px) 480px, 800px"
                alt="Aide à domicile Haute-Savoie"
                width="500"
                height="625"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                referrerPolicy="no-referrer"
                fetchPriority="high"
                decoding="sync"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  if (target.src !== "/1_11zon.webp") {
                    target.src = "/1_11zon.webp";
                  }
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            </div>
            {isAdmin && (
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <label className="bg-white/90 p-4 rounded-2xl shadow-xl cursor-pointer hover:bg-white transition-all flex items-center gap-2 font-bold text-primary" aria-label="Changer l'image hero">
                  <Plus className="size-5" aria-hidden="true" />
                  Changer la photo
                  <input 
                    type="file" 
                    className="hidden" 
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleImageUpload(file, (base64) => updateContent('hero_image', base64));
                    }}
                  />
                </label>
              </div>
            )}
            
            {/* Floating Badge */}
            <motion.div 
              animate={!isMobile ? { y: [0, -10, 0] } : false}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              className="absolute -bottom-6 -left-6 bg-white p-6 rounded-3xl shadow-2xl border border-slate-50 flex items-center gap-4"
            >
              <div className="size-12 bg-emerald-100 rounded-2xl flex items-center justify-center">
                <CheckCircle2 className="size-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-[10px] font-black text-slate-600 uppercase tracking-widest">Disponibilité</p>
                <p className="text-sm font-black text-slate-900">Intervention sous 48h</p>
              </div>
            </motion.div>
          </motion.div>
          </div>
        </section>

        {/* Partners Section */}
        <section className="bg-slate-50 py-20 border-y border-slate-100">
          <div className="max-w-7xl mx-auto px-6">
            <p className="text-center text-xs font-black text-slate-600 uppercase tracking-[0.2em] mb-12">Ils nous font confiance & financent vos prestations</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Conseil Départemental */}
              <div className="flex items-center gap-4 bg-secondary/5 p-6 rounded-2xl border-2 border-secondary/10 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all group">
                <div className="bg-secondary p-3 rounded-xl text-white group-hover:scale-110 transition-transform">
                  <Building2 className="size-6" />
                </div>
                <div className="flex flex-col">
                  <span className="text-[10px] font-black text-secondary uppercase tracking-widest leading-none mb-1">Institutionnel</span>
                  <span className="font-black text-slate-900 tracking-tighter text-sm lg:text-base">CONSEIL DÉPARTEMENTAL 74</span>
                </div>
              </div>

              {/* APA / PCH */}
              <div className="flex items-center gap-4 bg-primary/5 p-6 rounded-2xl border-2 border-primary/10 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all group">
                <div className="bg-primary p-3 rounded-xl text-white group-hover:scale-110 transition-transform">
                  <CheckCircle2 className="size-6" />
                </div>
                <div className="flex flex-col">
                  <span className="text-[10px] font-black text-primary uppercase tracking-widest leading-none mb-1">Aides Publiques</span>
                  <span className="font-black text-slate-900 tracking-tighter text-sm lg:text-base">APA / PCH</span>
                </div>
              </div>

              {/* Mutuelles */}
              <div className="flex items-center gap-4 bg-secondary/5 p-6 rounded-2xl border-2 border-secondary/10 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all group">
                <div className="bg-secondary p-3 rounded-xl text-white group-hover:scale-110 transition-transform">
                  <ShieldCheck className="size-6" />
                </div>
                <div className="flex flex-col">
                  <span className="text-[10px] font-black text-secondary uppercase tracking-widest leading-none mb-1">Prise en Charge</span>
                  <span className="font-black text-slate-900 tracking-tighter text-sm lg:text-base">MUTUELLES FR/CH</span>
                </div>
              </div>
              
              {/* ONU Pension Fund */}
              <div className="flex items-center gap-4 bg-primary/5 p-6 rounded-2xl border-2 border-primary/10 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all group">
                <div className="bg-primary p-3 rounded-xl text-white group-hover:scale-110 transition-transform">
                  <Globe className="size-6" />
                </div>
                <div className="flex flex-col">
                  <span className="text-[10px] font-black text-primary uppercase tracking-widest leading-none mb-1">International</span>
                  <span className="font-black text-slate-900 tracking-tighter text-sm lg:text-base">ONU PENSION FUND</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="bg-secondary py-16 text-white">
          <div className="max-w-7xl mx-auto px-6 grid grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="flex flex-col items-center text-center space-y-2">
              <div className="text-4xl lg:text-5xl font-black">24h/24</div>
              <p className="text-secondary-foreground/80 font-bold uppercase text-xs tracking-widest">Disponibilité</p>
            </div>
            <div className="flex flex-col items-center text-center space-y-2">
              <div className="text-4xl lg:text-5xl font-black">7j/7</div>
              <p className="text-secondary-foreground/80 font-bold uppercase text-xs tracking-widest">Engagement</p>
            </div>
            <div className="flex flex-col items-center text-center space-y-2">
              <div className="text-4xl lg:text-5xl font-black">100%</div>
              <p className="text-secondary-foreground/80 font-bold uppercase text-xs tracking-widest">Devis Gratuit</p>
            </div>
            <div className="flex flex-col items-center text-center space-y-2">
              <div className="text-4xl lg:text-5xl font-black">74</div>
              <p className="text-secondary-foreground/80 font-bold uppercase text-xs tracking-widest">Haute-Savoie</p>
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section id="services" className="bg-white py-24">
          <div className="max-w-7xl mx-auto px-6">
            <ScrollAnimate className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-black mb-4">Nos Services Autonomie</h2>
              <p className="text-slate-600 max-w-2xl mx-auto">Un accompagnement sur mesure pour favoriser le maintien à domicile sur toute la Haute-Savoie (Genevois, Chablais, etc.).</p>
            </ScrollAnimate>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
              {services?.map((service, index) => (
                <ScrollAnimate key={service.id} delay={index * 0.05}>
                  <div className="overflow-hidden rounded-xl border border-slate-100 hover:shadow-lg hover:-translate-y-1 transition-all group relative bg-white flex flex-col cursor-pointer h-32">
                    <div className="p-4 flex-1 flex flex-col items-center justify-center text-center relative h-full">
                      {isAdmin && (
                        <button 
                          onClick={async (e) => {
                            e.stopPropagation();
                            setDeleteConfirm({ id: service.id, type: 'service', title: service.title });
                          }}
                          className="absolute top-2 right-2 bg-white/90 p-1 rounded-full text-red-500 opacity-0 group-hover:opacity-100 transition-opacity shadow-sm z-20"
                          aria-label={`Supprimer le service ${service.title}`}
                        >
                          <Trash2 className="size-3" />
                        </button>
                      )}
                      
                      {/* Default View: Icon + Title */}
                      <div className="flex flex-col items-center group-hover:opacity-0 transition-opacity duration-300">
                        <div className={`${index % 2 === 0 ? 'bg-primary/10 text-primary group-hover:bg-primary' : 'bg-secondary/10 text-secondary group-hover:bg-secondary'} w-10 h-10 rounded-lg flex items-center justify-center mb-3 group-hover:text-white transition-all duration-300 shadow-sm`}>
                          {React.createElement(getIcon(service.icon), { className: "size-5" })}
                        </div>
                        <h3 className="text-xs font-black mb-1 line-clamp-2">{service.title}</h3>
                      </div>

                      {/* Hover View: Description */}
                      <div className={`absolute inset-0 p-4 flex items-center justify-center ${index % 2 === 0 ? 'bg-primary' : 'bg-secondary'} text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl`}>
                        <p className="text-[10px] font-medium leading-tight">{service.description}</p>
                      </div>
                    </div>
                  </div>
                </ScrollAnimate>
              ))}
              {isAdmin && (
                <button 
                  onClick={() => setShowAddService(true)}
                  className="p-4 min-h-[120px] rounded-xl border-2 border-dashed border-slate-200 flex flex-col items-center justify-center gap-2 text-slate-600 hover:border-primary hover:text-primary transition-all"
                >
                  <Plus className="size-5" />
                  <span className="font-bold text-[10px]">Ajouter</span>
                </button>
              )}
            </div>
          </div>
        </section>

        {/* Devis Section */}
        <section id="devis" className="py-24 bg-white relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(16,185,129,0.05),transparent_70%)]"></div>
          <div className="max-w-4xl mx-auto px-6 relative">
            <div className="text-center mb-16">
              <h2 className="text-4xl lg:text-5xl font-black mb-6">Demandez votre Devis Gratuit</h2>
              <div className="relative group/edit inline-block w-full max-w-2xl">
                <p className="text-slate-600 text-lg mx-auto">
                  {content.quote_subtitle}
                </p>
                {isAdmin && (
                  <button
                    onClick={() => {
                      const newValue = prompt('Modifier le sous-titre du devis :', content.quote_subtitle);
                      if (newValue !== null) updateContent('quote_subtitle', newValue);
                    }}
                    className="absolute -right-8 top-0 p-2 bg-white rounded-full shadow-lg opacity-0 group-hover/edit:opacity-100 transition-opacity"
                    aria-label="Modifier le sous-titre"
                  >
                    <Settings className="size-4 text-slate-600" />
                  </button>
                )}
              </div>
            </div>
            <div className="bg-slate-50 p-8 lg:p-12 rounded-[2.5rem] shadow-2xl border border-slate-100 min-h-[600px]">
              <Suspense fallback={<div className="h-[500px] flex flex-col gap-4 animate-pulse"><div className="h-10 bg-slate-200 rounded-xl w-3/4"></div><div className="h-64 bg-slate-200 rounded-xl"></div><div className="h-12 bg-slate-200 rounded-xl"></div></div>}>
                <QuoteForm />
              </Suspense>
            </div>
            
            <div className="mt-12 flex flex-col items-center gap-4">
              <div className="flex items-center gap-3 bg-primary/5 px-6 py-3 rounded-2xl border border-primary/10 shadow-sm">
                <Accessibility className="size-6 text-primary" />
                <p className="text-sm font-bold text-slate-700">
                  Nos documents sont disponibles en <span className="text-primary uppercase">Falc</span> : Facile à Lire et à Comprendre
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Recruitment Section */}
        <section id="recrutement" className="py-24 bg-slate-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div className="space-y-8">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary rounded-full text-xs font-black uppercase tracking-widest">
                  <Briefcase className="size-3" />
                  Nous recrutons
                </div>
                <h2 className="text-4xl lg:text-5xl font-black leading-tight">
                  Rejoignez une équipe <span className="text-primary">passionnée</span> et engagée
                </h2>
                <p className="text-lg text-slate-600 leading-relaxed">
                  Chez DOMO74, nous croyons que la qualité de l'accompagnement passe par l'épanouissement de nos collaborateurs. Nous recherchons des auxiliaires de vie, des aides ménagères et des professionnels du soin partageant nos valeurs de bienveillance et de respect.
                </p>
                <div className="grid sm:grid-cols-2 gap-6">
                  <div className="p-6 bg-white rounded-2xl border border-slate-100 shadow-sm">
                    <div className="size-10 bg-primary/10 rounded-xl flex items-center justify-center mb-4">
                      <Clock className="size-5 text-primary" />
                    </div>
                    <h4 className="font-bold mb-2">Flexibilité</h4>
                    <p className="text-sm text-slate-700">Des plannings adaptés à votre vie personnelle.</p>
                  </div>
                  <div className="p-6 bg-white rounded-2xl border border-slate-100 shadow-sm">
                    <div className="size-10 bg-primary/10 rounded-xl flex items-center justify-center mb-4">
                      <Star className="size-5 text-primary" />
                    </div>
                    <h4 className="font-bold mb-2">Valorisation</h4>
                    <p className="text-sm text-slate-700">Une rémunération attractive et des avantages sociaux.</p>
                  </div>
                </div>
              </div>
              <div className="bg-slate-900 p-8 lg:p-12 rounded-[3rem] shadow-2xl relative overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-primary/10 blur-3xl -mr-32 -mt-32"></div>
                <div className="relative z-10">
                  <h3 className="text-2xl font-black text-white mb-8">Postuler en 1 minute</h3>
                  <Suspense fallback={<div className="h-[400px] flex flex-col gap-4 animate-pulse"><div className="h-12 bg-white/10 rounded-xl"></div><div className="h-12 bg-white/10 rounded-xl"></div><div className="h-32 bg-white/10 rounded-xl"></div></div>}>
                    <RecruitmentForm />
                  </Suspense>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* About & Financing Section */}
        <section id="about" className="py-24 bg-secondary/5">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid lg:grid-cols-2 gap-16 items-start">
              <div className="space-y-8">
                <div className="relative rounded-3xl overflow-hidden shadow-xl mb-8 group animate-pro-frame">
                  <img 
                    src="/2_11zon.webp" 
                    srcSet="/2_11zon.webp 600w, /2_11zon.webp 1200w"
                    alt="Aide à domicile Haute-Savoie - Accompagnement humain et bienveillant Domo74"
                    width="800"
                    height="256"
                    className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-700"
                    referrerPolicy="no-referrer"
                    loading="lazy"
                    decoding="async"
                    fetchPriority="low"
                    style={{ contentVisibility: 'auto' }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent flex items-end p-8">
                    <p className="text-white font-bold italic">"La bienveillance au cœur de notre action quotidiennement."</p>
                  </div>
                  {isAdmin && (
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <label className="bg-white/90 p-4 rounded-2xl shadow-xl cursor-pointer hover:bg-white transition-all flex items-center gap-2 font-bold text-primary">
                        <Plus className="size-5" />
                        Changer la photo
                        <input 
                          type="file" 
                          className="hidden" 
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handleImageUpload(file, (base64) => updateContent('about_image', base64));
                          }}
                        />
                      </label>
                    </div>
                  )}
                </div>
                {isAdmin ? (
                  <input 
                    className="text-3xl lg:text-4xl font-black bg-white border border-primary/20 p-2 rounded-xl w-full"
                    value={content.about_title}
                    onChange={(e) => updateContent('about_title', e.target.value)}
                  />
                ) : (
                  <h2 className="text-3xl lg:text-4xl font-black text-slate-900">{content.about_title}</h2>
                )}
                
                {isAdmin ? (
                  <textarea 
                    className="text-lg text-slate-600 bg-white border border-primary/20 p-2 rounded-xl w-full h-48"
                    value={content.about_text}
                    onChange={(e) => updateContent('about_text', e.target.value)}
                  />
                ) : (
                  <p className="text-lg text-slate-600 leading-relaxed whitespace-pre-line">
                    {content.about_text}
                  </p>
                )}
              </div>

              <div className="bg-white p-8 lg:p-12 rounded-3xl shadow-xl border border-primary/10 space-y-8">
                <div className="flex items-center gap-4 text-secondary">
                  <div className="bg-secondary/10 p-3 rounded-2xl">
                    <CheckCircle2 className="size-8" />
                  </div>
                  <h3 className="text-2xl font-bold">Prise en charge & Engagement</h3>
                </div>

                {isAdmin ? (
                  <textarea 
                    className="text-slate-600 bg-white border border-primary/20 p-2 rounded-xl w-full h-64"
                    value={content.financing_text}
                    onChange={(e) => updateContent('financing_text', e.target.value)}
                  />
                ) : (
                  <p className="text-slate-600 leading-relaxed whitespace-pre-line">
                    {content.financing_text}
                  </p>
                )}

                <div className="bg-slate-50 border border-slate-100 rounded-2xl p-4 flex items-center gap-4">
                  <div className="bg-primary/10 p-2 rounded-lg text-primary">
                    <Accessibility className="size-5" />
                  </div>
                  <p className="text-xs font-medium text-slate-600">
                    Nos documents sont disponibles en <span className="font-black text-primary">FALC</span> : Facile à Lire et à Comprendre.
                  </p>
                </div>

                <div className="pt-4">
                  <a href="#devis" className="block w-full bg-secondary text-white py-4 rounded-xl font-bold text-lg shadow-lg shadow-secondary/20 hover:bg-secondary/90 transition-all text-center">
                    Demander mon devis gratuit
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SEO Rich Content Section */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="prose prose-slate max-w-none">
              <h2 className="text-3xl lg:text-4xl font-black text-slate-900 mb-8">Aide à domicile en Haute-Savoie : Un accompagnement sur mesure pour votre autonomie</h2>
              <div className="grid lg:grid-cols-2 gap-12 text-slate-600 leading-relaxed">
                <div className="space-y-6">
                  <p>
                    Le maintien à domicile est une priorité pour de nombreuses familles en Haute-Savoie. Chez <strong>Domo74</strong>, nous comprenons que rester chez soi, dans un environnement familier, est essentiel pour le bien-être physique et moral des personnes âgées ou en situation de handicap. Notre mission est de fournir un service d'<strong>aide à domicile à Annemasse</strong> et dans tout le département 74, alliant professionnalisme et chaleur humaine.
                  </p>
                  <h3 className="text-2xl font-bold text-slate-900">Services d'aide à domicile : Une présence quotidienne</h3>
                  <p>
                    Nos auxiliaires de vie interviennent pour faciliter les gestes du quotidien. Que ce soit pour l'aide au lever, à la toilette, ou à la préparation des repas, nous veillons à ce que chaque bénéficiaire conserve sa dignité et son confort. L'<strong>aide à domicile en Haute-Savoie</strong> ne se limite pas aux tâches ménagères ; c'est un véritable lien social qui se crée entre l'intervenant et la personne accompagnée.
                  </p>
                  <h3 className="text-2xl font-bold text-slate-900">Accompagnement des personnes âgées et maintien à domicile</h3>
                  <p>
                    Le vieillissement de la population en Haute-Savoie nécessite des solutions adaptées. Le <strong>maintien à domicile des personnes âgées</strong> permet d'éviter ou de retarder l'entrée en institution. Nos services incluent une veille attentive, une aide à la mobilité et un soutien moral constant. Nous travaillons en étroite collaboration avec les familles et les professionnels de santé pour assurer une prise en charge globale et sécurisante.
                  </p>
                </div>
                <div className="space-y-6">
                  <h3 className="text-2xl font-bold text-slate-900">Aide aux personnes handicapées : Favoriser l'inclusion</h3>
                  <p>
                    L'autonomie des personnes en situation de handicap est au cœur de nos préoccupations. Nous proposons des interventions spécifiques, adaptées à chaque type de handicap, pour favoriser l'inclusion sociale et faciliter la vie quotidienne. Nos équipes sont formées aux techniques de transfert et à l'utilisation du matériel médicalisé, garantissant une sécurité optimale.
                  </p>
                  <h3 className="text-2xl font-bold text-slate-900">Pourquoi choisir Domo74 en Haute-Savoie ?</h3>
                  <p>
                    En tant qu'acteur local basé à Gaillard, nous rayonnons sur tout le bassin Genevois et la Haute-Savoie. Notre connaissance du territoire nous permet d'être réactifs et proches de nos bénéficiaires. Nous sommes autorisés par le Conseil Départemental, ce qui vous permet de bénéficier d'aides financières telles que l'APA (Allocation Personnalisée d'Autonomie) ou la PCH (Prestation de Compensation du Handicap).
                  </p>
                  <p>
                    Faire appel à Domo74, c'est choisir une structure à taille humaine où chaque situation est étudiée avec soin. Nous réalisons une évaluation gratuite à domicile pour définir ensemble le plan d'aide le plus adapté à vos besoins et à votre budget.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-24 bg-slate-50">
          <div className="max-w-4xl mx-auto px-6">
            <h2 className="text-3xl lg:text-4xl font-black text-center mb-16">Questions Fréquentes (FAQ) - Aide à domicile 74</h2>
            <div className="space-y-6">
              {[
                {
                  q: "Quelles sont les aides financières disponibles pour l'aide à domicile en Haute-Savoie ?",
                  a: "Vous pouvez bénéficier de l'APA (Allocation Personnalisée d'Autonomie) pour les personnes âgées de plus de 60 ans, ou de la PCH (Prestation de Compensation du Handicap). De plus, nos services ouvrent droit à un crédit d'impôt de 50%."
                },
                {
                  q: "Intervenez-vous le week-end et les jours fériés ?",
                  a: "Oui, Domo74 assure une continuité de service 24h/24 et 7j/7, y compris les dimanches et jours fériés, pour garantir la sécurité et le bien-être de nos bénéficiaires."
                },
                {
                  q: "Comment est sélectionné le personnel d'aide à domicile ?",
                  a: "Nos auxiliaires de vie sont recrutés pour leurs compétences techniques (diplômes, expérience) mais aussi pour leurs qualités humaines. Nous effectuons des contrôles réguliers pour assurer une qualité de service constante."
                },
                {
                  q: "Quelle est la zone d'intervention de Domo74 ?",
                  a: "Nous intervenons dans toute la Haute-Savoie, avec une présence forte sur Annemasse, Gaillard, Ambilly, Ville-la-Grand, et tout le bassin Genevois. Depuis août 2025, nous couvrons également le secteur du Chablais."
                }
              ].map((item, i) => (
                <div key={i} className="bg-white p-8 rounded-2xl shadow-sm border border-slate-100">
                  <h4 className="font-bold text-lg text-slate-900 mb-4">{item.q}</h4>
                  <p className="text-slate-600 leading-relaxed">{item.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* News / Blog Section */}
        <section id="news" className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex justify-between items-end mb-16">
              <div>
                <h2 className="text-3xl lg:text-4xl font-black mb-4">Actualités & Conseils</h2>
                <p className="text-slate-600">Retrouvez nos derniers articles sur l'aide à domicile et le maintien de l'autonomie.</p>
              </div>
              {isAdmin && (
                <button 
                  onClick={() => setShowAddNews(true)}
                  className="bg-primary text-white px-6 py-3 rounded-xl font-bold flex items-center gap-2 hover:bg-primary/90 transition-all"
                >
                  <Plus className="size-5" />
                  Nouvel article
                </button>
              )}
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {news?.map((item, index) => (
                <ScrollAnimate key={item.id} delay={index * 0.1}>
                  <article className="group bg-white rounded-3xl border border-slate-100 overflow-hidden hover:shadow-2xl transition-all duration-500">
                    <div className="relative h-48 overflow-hidden animate-pro-frame">
                      <img 
                        src={item.image} 
                        alt={item.title} 
                        width="400"
                        height="192"
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                        referrerPolicy="no-referrer"
                      />
                      <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-md px-3 py-1 rounded-full text-xs font-bold text-primary uppercase tracking-wider">
                        {item.category}
                      </div>
                      {isAdmin && (
                        <button 
                          onClick={() => setDeleteConfirm({ id: item.id, type: 'news', title: item.title })}
                          className="absolute top-4 right-4 bg-red-500 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                          aria-label={`Supprimer l'article ${item.title}`}
                        >
                          <Trash2 className="size-4" />
                        </button>
                      )}
                    </div>
                    <div className="p-8">
                      <p className="text-xs font-bold text-slate-600 mb-2 uppercase tracking-widest">{new Date(item.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
                      <h3 className="text-xl font-bold mb-4 group-hover:text-primary transition-colors">{item.title}</h3>
                      <p className="text-slate-600 text-sm leading-relaxed line-clamp-3 mb-6">{item.content}</p>
                      <button className="flex items-center gap-2 text-primary font-bold text-sm group/btn" aria-label={`Lire la suite de l'article ${item.title}`}>
                        Lire la suite
                        <ChevronRight className="size-4 group-hover/btn:translate-x-1 transition-transform" />
                      </button>
                    </div>
                  </article>
                </ScrollAnimate>
              ))}
            </div>
          </div>
        </section>

        {/* Communes Section */}
        <section className="py-24 bg-white border-t border-slate-100 overflow-hidden">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid lg:grid-cols-3 gap-16 items-center">
              <div className="lg:col-span-1 space-y-6">
                <h2 className="text-3xl font-black text-slate-900 leading-tight">Notre Zone d'Intervention</h2>
                <p className="text-slate-600 leading-relaxed">
                  Domo74 intervient principalement sur l'ensemble du <strong>Genevois</strong> et du <strong>Chablais</strong>. 
                  Grâce à nos deux bureaux stratégiquement situés, nous garantissons une proximité et une réactivité maximale pour toutes vos demandes d'aide à domicile.
                </p>
                <div className="space-y-4">
                  <div className="flex items-center gap-4 p-4 rounded-2xl bg-slate-50 border border-slate-100">
                    <div className="bg-primary text-white p-2 rounded-lg">
                      <MapPin className="size-5" />
                    </div>
                    <div>
                      <p className="text-xs font-black uppercase tracking-wider">Genevois</p>
                      <p className="text-[10px] text-slate-700">Bureau d'Annemasse</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 p-4 rounded-2xl bg-slate-50 border border-slate-100">
                    <div className="bg-primary text-white p-2 rounded-lg">
                      <MapPin className="size-5" />
                    </div>
                    <div>
                      <p className="text-xs font-black uppercase tracking-wider">Chablais</p>
                      <p className="text-[10px] text-slate-700">Bureau de Veigy-Foncenex</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="lg:col-span-2">
                <ZoneMap isMobile={isMobile} />
              </div>
            </div>

            {/* Hidden Communes for SEO only */}
            <div className="sr-only" aria-hidden="true">
              <h3>Villes desservies en Haute-Savoie (74)</h3>
              {["Abondance", "Alby-sur-Chéran", "Alex", "Allèves", "Allinges", "Allonzier-la-Caille", "Amancy", "Ambilly", "Andilly", "Annecy", "Annemasse", "Anthy-sur-Léman", "Arâches-la-Frasse", "Arbusigny", "Archamps", "Arenthon", "Argonay", "Armoy", "Arthaz-Pont-Notre-Dame", "Ayse", "Ballaison", "Bassy", "Beaumont", "Bellevaux", "Bernex", "Bloye", "Bluffy", "Boëge", "Bogève", "Bonne", "Bonneguête", "Bonnevaux", "Bonneville", "Bons-en-Chablais", "Bossey", "Boussy", "Brenthonne", "Brizon", "Burdignin", "Cercier", "Cernex", "Cervens", "Chainaz-les-Frasses", "Chamonix-Mont-Blanc", "Champanges", "Chapeiry", "Charvonnex", "Châtel", "Châtillon-sur-Cluses", "Chaumont", "Chavannaz", "Chavanod", "Chêne-en-Semine", "Chenex", "Chens-sur-Léman", "Chessenaz", "Chevaline", "Chevenoz", "Chevrier", "Chilly", "Choisy", "Clarafond-Arcine", "Clermont", "Cluses", "Collonges-sous-Salève", "Combloux", "Cons-Sainte-Colombe", "Contamine-Sarzin", "Contamine-sur-Arve", "Copponex", "Cordon", "Cornier", "Cranves-Sales", "Crempigny-Bonneguête", "Cruseilles", "Cusy", "Cuvat", "Demi-Quartier", "Desingy", "Dingy-en-Vuache", "Dingy-Saint-Clair", "Domancy", "Doussard", "Douvaine", "Draillant", "Droisy", "Duingt", "Éloise", "Entrevernes", "Épagny-Metz-Tessy", "Étercy", "Étrembières", "Évian-les-Bains", "Évires", "Excenevex", "Faucigny", "Faverges-Seythenex", "Feigères", "Fessy", "Féternes", "Fillinges", "Franclens", "Frangy", "Gaillard", "Giez", "Groisy", "Gruffy", "Habère-Lullin", "Habère-Poche", "Hauteville-sur-Fier", "Héry-sur-Alby", "JonZier-Épagny", "Juvigny", "La Balme-de-Sillingy", "La Balme-de-Thuy", "La Baume", "La Chapelle-d'Abondance", "La Chapelle-Saint-Maurice", "La Clusaz", "La Côte-d'Arbroz", "La Forclaz", "La Muraz", "La Rivière-Enverse", "La Roche-sur-Foron", "La Tour", "La Vernaz", "Lathuile", "Le Biot", "Le Bouchet-Mont-Charvin", "Le Grand-Bornand", "Le Petit-Bornand-les-Glières", "Le Reposoir", "Le Sappey", "Leschaux", "Loisin", "Lornay", "Lovagny", "Lucinges", "Lugrin", "Lullin", "Lully", "Lyaud", "Machilly", "Magland", "Manigod", "Marcellaz", "Marcellaz-Albanais", "Margencel", "Marignier", "Marigny-Saint-Marcel", "Marin", "Marlioz", "Marnaz", "Massingy", "Massongy", "Maxilly-sur-Léman", "Megève", "Mégevette", "Meillerie", "Menthon-Saint-Bernard", "Menthonnex-en-Bornes", "Menthonnex-sous-Clermont", "Messery", "Metz-Tessy", "Meythet", "Mieussy", "Minzier", "Monnetier-Mornex", "Mont-Saxonnex", "Montagny-les-Lanches", "Montriond", "Morbier", "Morillon", "Morzine", "Moye", "Mûres", "Musièges", "Nancy-sur-Cluses", "Nangy", "Nâves-Parmelan", "Nernier", "Neuvecelle", "Neydens", "Nonglard", "Novel", "Onnion", "Orcier", "Passy", "Peillonnex", "Perrignier", "Pers-Jussy", "Poisy", "Praz-sur-Arly", "Présilly", "Pringy", "Publier", "Quintal", "Reignier-Ésery", "Reyvroz", "Rumilly", "Saint-André-de-Boëge", "Saint-Blaise", "Saint-Cergues", "Saint-Eusèbe", "Saint-Eustache", "Saint-Félix", "Saint-Ferréol", "Saint-Germain-sur-Rhône", "Saint-Gervais-les-Bains", "Saint-Gingolph", "Saint-Jean-d'Aulps", "Saint-Jean-de-Sixt", "Saint-Jean-de-Tholome", "Saint-Jeoire", "Saint-Jorioz", "Saint-Julien-en-Genevois", "Saint-Laurent", "Saint-Martin-Bellevue", "Saint-Pierre-en-Faucigny", "Saint-Sigismond", "Saint-Sixt", "Saint-Sylvestre", "Sales", "Sallanches", "Sallenôves", "Samoëns", "Savigny", "Saxel", "Scientrier", "Sciez", "Scionzier", "Servoz", "Sévrier", "Seyssel", "Seytroux", "Sixt-Fer-à-Cheval", "Talloires-Montmin", "Taninges", "Thollon-les-Mémises", "Thônes", "Thonon-les-Bains", "Thorens-Glières", "Thusy", "Thyez", "Valleiry", "Vallières-sur-Fier", "Vallorcine", "Vanzy", "Vaulx", "Veigy-Foncenex", "Versonnex", "Vétraz-Monthoux", "Veyrier-du-Lac", "Villard", "Villards-sur-Thônes", "Villaz", "Ville-en-Sallaz", "Ville-la-Grand", "Villy-le-Bouveret", "Villy-le-Pelloux", "Vinzier", "Viry", "Viuz-en-Sallaz", "Viuz-la-Chiésaz", "Vougy", "Vovray-en-Bornes", "Vulbens", "Yvoire"].map(city => (
                <span key={city}>{city}, </span>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section id="contact" className="py-24 bg-slate-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div className="space-y-8">
                <h2 className="text-3xl lg:text-4xl font-black mb-8">Contactez Domo74 (Haute-Savoie)</h2>
                <div className="relative rounded-2xl overflow-hidden h-64 group animate-pro-frame">
                  <img 
                    src={content.contact_image} 
                    width="800"
                    height="256"
                    className="w-full h-full object-cover" 
                    alt="Aide à domicile Annemasse et Haute-Savoie - Zone d'intervention Domo74"
                    referrerPolicy="no-referrer"
                  />
                  <div className="absolute inset-0 bg-primary/20 backdrop-blur-[2px]"></div>
                  
                  {/* Animated Zone Markers */}
                  <div className="absolute inset-0 p-6 flex flex-col justify-between">
                    <motion.div 
                      initial={false}
                      animate={!isMobile ? { x: 0, opacity: 1 } : { x: 0, opacity: 1 }}
                      transition={!isMobile ? { delay: 0.2 } : { duration: 0 }}
                      className="flex items-center gap-3 bg-white/95 backdrop-blur-sm p-3 rounded-2xl shadow-xl w-fit self-start border border-white/50"
                    >
                      <div className="relative">
                        <div className="size-3 bg-primary rounded-full"></div>
                        <div className="absolute inset-0 size-3 bg-primary rounded-full animate-ping"></div>
                      </div>
                      <div>
                        <p className="text-[10px] font-black uppercase tracking-wider text-slate-900">Bureau du Genevois</p>
                        <p className="text-[9px] font-bold text-primary">Annemasse</p>
                      </div>
                    </motion.div>

                    <motion.div 
                      initial={false}
                      animate={!isMobile ? { x: 0, opacity: 1 } : { x: 0, opacity: 1 }}
                      transition={!isMobile ? { delay: 0.2 } : { duration: 0 }}
                      className="flex items-center gap-3 bg-white/95 backdrop-blur-sm p-3 rounded-2xl shadow-xl w-fit self-end border border-white/50"
                    >
                      <div className="text-right">
                        <p className="text-[10px] font-black uppercase tracking-wider text-slate-900">Bureau du Chablais</p>
                        <p className="text-[9px] font-bold text-secondary">Veigy-Foncenex</p>
                      </div>
                      <div className="relative">
                        <div className="size-3 bg-secondary rounded-full"></div>
                        <div className="absolute inset-0 size-3 bg-secondary rounded-full animate-ping"></div>
                      </div>
                    </motion.div>
                  </div>

                  {/* Zone Coverage Animation */}
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden">
                    <motion.div 
                      animate={!isMobile ? { 
                        scale: [1, 1.5, 1],
                        opacity: [0.1, 0.2, 0.1]
                      } : false}
                      transition={!isMobile ? { duration: 8, repeat: Infinity, ease: "easeInOut" } : { duration: 0 }}
                      className="w-full h-full border-[40px] border-white/10 rounded-full"
                    />
                  </div>

                  {isAdmin && (
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <label className="bg-white/90 p-4 rounded-2xl shadow-xl cursor-pointer hover:bg-white transition-all flex items-center gap-2 font-bold text-primary" aria-label="Changer l'image de contact">
                        <Plus className="size-5" aria-hidden="true" />
                        Changer la photo
                        <input 
                          type="file" 
                          className="hidden" 
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handleImageUpload(file, (base64) => updateContent('contact_image', base64));
                          }}
                        />
                      </label>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="grid sm:grid-cols-2 lg:grid-cols-1 gap-8">
                <address className="not-italic grid sm:grid-cols-2 lg:grid-cols-1 gap-8">
                <div className="flex gap-4 p-6 bg-white rounded-2xl shadow-sm border border-slate-100">
                  <div className="bg-secondary text-white p-3 rounded-xl h-fit">
                    <Phone className="size-6" />
                  </div>
                  <div>
                    <h4 className="font-bold text-lg">Téléphone</h4>
                    {isAdmin ? (
                      <input 
                        className="text-slate-600 bg-white border border-secondary/20 p-1 rounded mt-1"
                        value={content.contact_phone}
                        onChange={(e) => updateContent('contact_phone', e.target.value)}
                      />
                    ) : (
                      <a href={`tel:${content.contact_phone}`} className="text-slate-600 hover:text-secondary transition-colors">{content.contact_phone}</a>
                    )}
                  </div>
                </div>
                <div className="flex gap-4 p-6 bg-white rounded-2xl shadow-sm border border-slate-100">
                  <div className="bg-primary text-white p-3 rounded-xl h-fit">
                    <Mail className="size-6" />
                  </div>
                  <div>
                    <h4 className="font-bold text-lg">Email</h4>
                    {isAdmin ? (
                      <input 
                        className="text-slate-600 bg-white border border-primary/20 p-1 rounded mt-1"
                        value={content.contact_email}
                        onChange={(e) => updateContent('contact_email', e.target.value)}
                      />
                    ) : (
                      <a href={`mailto:${content.contact_email}`} className="text-slate-600 hover:text-primary transition-colors">{content.contact_email}</a>
                    )}
                  </div>
                </div>
                <div className="flex gap-4 p-6 bg-white rounded-2xl shadow-sm border border-slate-100">
                  <div className="bg-secondary text-white p-3 rounded-xl h-fit">
                    <MapPin className="size-6" />
                  </div>
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-bold text-lg">Bureau Genevois (Annemasse)</h4>
                      {isAdmin ? (
                        <input 
                          className="text-slate-600 bg-white border border-secondary/20 p-1 rounded mt-1 w-full"
                          value={content.contact_address_annemasse}
                          onChange={(e) => updateContent('contact_address_annemasse', e.target.value)}
                        />
                      ) : (
                        <p className="text-slate-600">{content.contact_address_annemasse}</p>
                      )}
                    </div>
                    <div>
                      <h4 className="font-bold text-lg">Bureau Chablais (Veigy-Foncenex)</h4>
                      {isAdmin ? (
                        <input 
                          className="text-slate-600 bg-white border border-secondary/20 p-1 rounded mt-1 w-full"
                          value={content.contact_address_veigy}
                          onChange={(e) => updateContent('contact_address_veigy', e.target.value)}
                        />
                      ) : (
                        <p className="text-slate-600">{content.contact_address_veigy}</p>
                      )}
                    </div>
                  </div>
                </div>
                </address>
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className="bg-slate-900 text-slate-300 py-16">
        <div className="max-w-7xl mx-auto px-6">
          {isAdmin && (
            <div className="mb-20 bg-white/5 p-8 rounded-3xl border border-white/10">
              <div className="flex items-center gap-4 mb-8">
                <div className="size-12 bg-secondary/20 rounded-2xl flex items-center justify-center">
                  <Settings className="size-6 text-secondary" />
                </div>
                <div>
                  <h4 className="text-white font-bold">Configuration Système</h4>
                  <p className="text-xs text-slate-600">Paramètres globaux de l'application</p>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white/5 p-6 rounded-2xl border border-white/5">
                  <label className="block text-xs font-bold text-slate-600 uppercase tracking-widest mb-2">Code Agence Xelya (Ximi)</label>
                  <div className="flex gap-2">
                    <input 
                      type="text"
                      className="flex-1 bg-slate-800 border border-white/10 rounded-xl px-4 py-2 text-white outline-none focus:border-primary transition-all"
                      value={content.agency_code}
                      onChange={(e) => updateContent('agency_code', e.target.value)}
                    />
                    <div className="px-4 py-2 bg-slate-700 rounded-xl text-slate-600 text-sm flex items-center">
                      Actuel: {content.agency_code}
                    </div>
                  </div>
                  <p className="text-[10px] text-slate-700 mt-2 italic">Par défaut: do74. Ce code est utilisé pour construire les URLs de l'extranet.</p>
                </div>
                <div className="bg-white/5 p-6 rounded-2xl border border-white/5">
                  <label className="block text-xs font-bold text-slate-600 uppercase tracking-widest mb-2">Google Site Verification</label>
                  <input 
                    type="text"
                    placeholder="ex: google-site-verification-code"
                    className="w-full bg-slate-800 border border-white/10 rounded-xl px-4 py-2 text-white outline-none focus:border-primary transition-all"
                    value={content.google_verification_code}
                    onChange={(e) => updateContent('google_verification_code', e.target.value)}
                  />
                  <p className="text-[10px] text-slate-700 mt-2 italic">Code pour Google Search Console (identification du site).</p>
                </div>
                <div className="bg-white/5 p-6 rounded-2xl border border-white/5">
                  <label className="block text-xs font-bold text-slate-600 uppercase tracking-widest mb-2">Google Ads / Analytics Code</label>
                  <textarea 
                    rows={3}
                    placeholder="ID (ex: AW-12345) ou snippet complet <script>..."
                    className="w-full bg-slate-800 border border-white/10 rounded-xl px-4 py-2 text-white outline-none focus:border-primary transition-all text-xs"
                    value={content.google_ads_code}
                    onChange={(e) => updateContent('google_ads_code', e.target.value)}
                  />
                  <p className="text-[10px] text-slate-700 mt-2 italic">Collez votre ID Google Ads/Analytics ou le script complet fourni par Google.</p>
                </div>
              </div>
            </div>
          )}
          <div className="grid md:grid-cols-3 gap-12 mb-12">
            <div className="space-y-4">
              <a href="/" className="flex items-center gap-3 group">
                <div className="bg-white p-1 rounded-lg w-12 h-12 flex items-center justify-center shadow-sm group-hover:scale-105 transition-transform">
                  <img 
                    src="/logo.webp" 
                    alt="DOMO74 Logo" 
                    width="48" 
                    height="48" 
                    className="w-full h-full object-contain" 
                    referrerPolicy="no-referrer" 
                  />
                </div>
                <div className="text-2xl font-black text-white tracking-tighter">DOMO74</div>
              </a>
              <p className="text-sm leading-relaxed">Service Autonomie dédié à l’accompagnement quotidien sur toute la Haute-Savoie. Présence humaine et professionnelle 24h/24, 7j/7.</p>
              <div className="flex items-center gap-2 text-secondary font-bold">
                <Phone className="size-4" />
                <a href={`tel:${content?.contact_phone || '04 56 30 74 06'}`} className="hover:underline">{content?.contact_phone || '04 56 30 74 06'}</a>
              </div>
              <div className="flex items-center gap-2 text-primary text-[10px] font-bold uppercase tracking-widest bg-white/5 w-fit px-3 py-1 rounded-full border border-white/10">
                <Accessibility className="size-3" />
                <span>Documents disponibles en FALC</span>
              </div>
            </div>
            <div className="space-y-4">
              <h4 className="text-white font-bold">Navigation</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#services" className="hover:text-secondary transition-colors">Nos Services</a></li>
                <li><a href="#about" className="hover:text-secondary transition-colors">À Propos</a></li>
                <li><a href="#recrutement" className="hover:text-secondary transition-colors">Recrutement</a></li>
                <li><a href="#contact" className="hover:text-secondary transition-colors">Contact</a></li>
              </ul>
            </div>
            <div className="space-y-4">
              <h4 className="text-white font-bold">Légal</h4>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => setActiveLegal('mentions')} className="hover:text-secondary transition-colors cursor-pointer">Mentions Légales</button></li>
                <li><button onClick={() => setActiveLegal('privacy')} className="hover:text-secondary transition-colors cursor-pointer">Politique de Confidentialité</button></li>
                <li><button onClick={() => setActiveLegal('cgv')} className="hover:text-secondary transition-colors cursor-pointer">Conditions Générales</button></li>
                <li><button onClick={() => setActiveLegal('cookies')} className="hover:text-secondary transition-colors cursor-pointer">Politique Cookies</button></li>
              </ul>
            </div>
            <div className="space-y-4">
              <h4 className="text-white font-bold">Suivez-nous</h4>
              <p className="text-xs leading-relaxed">Retrouvez nos actualités et conseils sur les réseaux sociaux.</p>
              <a 
                href="https://www.facebook.com/people/DOMO-74/61574180212946/?locale=sv_SE#" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-3 bg-[#1877F2] text-white px-6 py-3 rounded-xl font-bold hover:bg-[#166fe5] transition-all w-fit group"
              >
                <Facebook className="size-5 fill-current group-hover:scale-110 transition-transform" />
                <span>Page Facebook DOMO74</span>
              </a>
              <div className="mt-4 rounded-xl overflow-hidden border border-white/10 bg-white/5 p-2">
                <iframe 
                  src="https://www.facebook.com/plugins/page.php?href=https%3A%2F%2Fwww.facebook.com%2Fpeople%2FDOMO-74%2F61574180212946%2F&tabs=timeline&width=340&height=330&small_header=true&adapt_container_width=true&hide_cover=false&show_facepile=true&appId" 
                  width="100%" 
                  height="330" 
                  style={{ border: 'none', overflow: 'hidden' }} 
                  scrolling="no" 
                  frameBorder="0" 
                  allowFullScreen={true} 
                  allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                  title="Facebook DOMO74"
                ></iframe>
              </div>
            </div>
          </div>
          <div className="pt-12 border-t border-slate-800">
            <h4 className="text-white font-bold mb-6 text-center">Nos zones d'intervention en Haute-Savoie</h4>
            <div className="flex flex-wrap justify-center gap-x-4 gap-y-2 text-[10px] text-slate-600">
              {[
                "Amancy", "Ambilly", "Annemasse", "Anthy-sur-Léman", "Archamps", "Arenthon",
                "Arthaz-Pont-Notre-Dame", "Ballaison", "Beaumont", "Bonne", "Bonneville",
                "Bons-en-Chablais", "Bossey", "Chens-sur-Léman", "Collonges-sous-Salève",
                "Contamine-sur-Arve", "Cranves-Sales", "Cruseilles", "Douvaine", "Étrembières",
                "Évian-les-Bains", "Excenevex", "Fillinges", "Frangy", "Gaillard", "Juvigny",
                "Loisin", "Lucinges", "Machilly", "Nangy", "Neydens", "Pers-Jussy",
                "Reignier-Ésery", "Saint-Cergues", "Saint-Julien-en-Genevois", "Sciez",
                "Thonon-les-Bains", "Thusy", "Thyez", "Valleiry", "Veigy-Foncenex",
                "Vétraz-Monthoux", "Ville-la-Grand", "Viry", "Vulbens", "Yvoire"
              ].map((city) => (
                <span key={city} className="hover:text-primary transition-colors">
                  aide à domicile à {city}
                </span>
              ))}
            </div>
          </div>
          <div className="pt-8 border-t border-slate-800 text-center mt-8">
            <p className="text-xs">© 2024 DOMO74 - Tous droits réservés. Autorisé par le Conseil Départemental de la Haute-Savoie.</p>
          </div>
        </div>
      </footer>

      <AnimatePresence>
        {activeLegal && (
          <Suspense fallback={null}>
            <LegalModal type={activeLegal} onClose={() => setActiveLegal(null)} />
          </Suspense>
        )}
        {/* Modals */}
        {deleteConfirm && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-6">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="absolute inset-0 bg-slate-900/80 backdrop-blur-sm"
              onClick={() => setDeleteConfirm(null)}
            />
            <motion.div 
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="relative bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl"
            >
              <div className="size-16 bg-red-100 text-red-600 rounded-full flex items-center justify-center mb-6 mx-auto">
                <Trash2 className="size-8" />
              </div>
              <h3 className="text-xl font-black text-center mb-2">Confirmer la suppression</h3>
              <p className="text-slate-600 text-center mb-8">
                Êtes-vous sûr de vouloir supprimer "{deleteConfirm.title}" ? Cette action est irréversible.
              </p>
              <div className="flex gap-4">
                <button 
                  onClick={() => setDeleteConfirm(null)}
                  className="flex-1 px-6 py-3 rounded-xl font-bold text-slate-600 hover:bg-slate-50 transition-all"
                  aria-label="Annuler la suppression"
                >
                  Annuler
                </button>
                <button 
                  onClick={handleDelete}
                  className="flex-1 px-6 py-3 rounded-xl font-bold bg-red-600 text-white hover:bg-red-700 transition-all shadow-lg shadow-red-200"
                  aria-label="Confirmer la suppression définitive"
                >
                  Supprimer
                </button>
              </div>
            </motion.div>
          </div>
        )}

        {showAddService && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-6">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="absolute inset-0 bg-slate-900/80 backdrop-blur-sm"
              onClick={() => setShowAddService(false)}
            />
            <motion.div 
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="relative bg-white rounded-3xl p-8 max-w-lg w-full shadow-2xl max-h-[90vh] overflow-y-auto"
            >
              <h3 className="text-2xl font-black mb-6">Ajouter un service</h3>
              <form onSubmit={handleAddService} className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-bold text-slate-700">Titre</label>
                  <input 
                    type="text" 
                    required
                    className="w-full p-3 rounded-xl border border-slate-200 outline-none focus:border-primary transition-all"
                    value={newService.title}
                    onChange={(e) => setNewService({ ...newService, title: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-bold text-slate-700">Description</label>
                  <textarea 
                    required
                    rows={3}
                    className="w-full p-3 rounded-xl border border-slate-200 outline-none focus:border-primary transition-all"
                    value={newService.description}
                    onChange={(e) => setNewService({ ...newService, description: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-bold text-slate-700">Icône</label>
                  <select 
                    className="w-full p-3 rounded-xl border border-slate-200 outline-none focus:border-primary transition-all"
                    value={newService.icon}
                    onChange={(e) => setNewService({ ...newService, icon: e.target.value })}
                  >
                    <option value="bed">Lit (Aide au lever/coucher)</option>
                    <option value="shirt">Vêtement (Aide à l'habillage)</option>
                    <option value="bath">Baignoire (Aide à la toilette)</option>
                    <option value="restaurant">Couverts (Préparation repas)</option>
                    <option value="directions_car">Voiture (Accompagnement)</option>
                    <option value="shopping_cart">Caddie (Courses)</option>
                    <option value="medical_services">Médical (Suivi)</option>
                    <option value="nightlight">Lune (Garde de nuit)</option>
                    <option value="timer">Chronomètre (Réactivité)</option>
                    <option value="psychology">Cerveau (Ateliers mémoire)</option>
                  </select>
                </div>
                <div className="flex gap-4 mt-8">
                  <button 
                    type="button"
                    onClick={() => setShowAddService(false)}
                    className="flex-1 px-6 py-3 rounded-xl font-bold text-slate-600 hover:bg-slate-50 transition-all"
                  >
                    Annuler
                  </button>
                  <button 
                    type="submit"
                    className="flex-1 px-6 py-3 rounded-xl font-bold bg-primary text-white hover:bg-primary/90 transition-all shadow-lg shadow-primary/20"
                  >
                    Ajouter
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}

        {showAddNews && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-6">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="absolute inset-0 bg-slate-900/80 backdrop-blur-sm"
              onClick={() => setShowAddNews(false)}
            />
            <motion.div 
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="relative bg-white rounded-3xl p-8 max-w-2xl w-full shadow-2xl max-h-[90vh] overflow-y-auto"
            >
              <h3 className="text-2xl font-black mb-6">Nouvel article</h3>
              <form onSubmit={handleAddNews} className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-bold text-slate-700">Titre</label>
                  <input 
                    type="text" 
                    required
                    className="w-full p-3 rounded-xl border border-slate-200 outline-none focus:border-primary transition-all"
                    value={newNews.title}
                    onChange={(e) => setNewNews({ ...newNews, title: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-bold text-slate-700">Contenu</label>
                  <textarea 
                    required
                    rows={5}
                    className="w-full p-3 rounded-xl border border-slate-200 outline-none focus:border-primary transition-all"
                    value={newNews.content}
                    onChange={(e) => setNewNews({ ...newNews, content: e.target.value })}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-bold text-slate-700">Catégorie</label>
                    <select 
                      className="w-full p-3 rounded-xl border border-slate-200 outline-none focus:border-primary transition-all"
                      value={newNews.category}
                      onChange={(e) => setNewNews({ ...newNews, category: e.target.value })}
                    >
                      <option value="Actualités">Actualités</option>
                      <option value="Conseils">Conseils</option>
                      <option value="Santé">Santé</option>
                      <option value="Accompagnement">Accompagnement</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-bold text-slate-700">Image (URL Unsplash)</label>
                    <input 
                      type="url" 
                      required
                      placeholder="https://images.unsplash.com/..."
                      className="w-full p-3 rounded-xl border border-slate-200 outline-none focus:border-primary transition-all"
                      value={newNews.image}
                      onChange={(e) => setNewNews({ ...newNews, image: e.target.value })}
                    />
                  </div>
                </div>
                <div className="flex gap-4 mt-8">
                  <button 
                    type="button"
                    onClick={() => setShowAddNews(false)}
                    className="flex-1 px-6 py-3 rounded-xl font-bold text-slate-600 hover:bg-slate-50 transition-all"
                  >
                    Annuler
                  </button>
                  <button 
                    type="submit"
                    className="flex-1 px-6 py-3 rounded-xl font-bold bg-primary text-white hover:bg-primary/90 transition-all shadow-lg shadow-primary/20"
                  >
                    Publier
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}

        {showLogin && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-6">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowLogin(false)}
              className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
            />
            <motion.div 
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative bg-white p-8 rounded-3xl shadow-2xl w-full max-w-md"
            >
              <button onClick={() => setShowLogin(false)} className="absolute top-6 right-6 text-slate-600 hover:text-slate-800" aria-label="Fermer la fenêtre de connexion">
                <X className="size-6" />
              </button>
              <h2 className="text-2xl font-black mb-6">Administration</h2>
              {!show2FA ? (
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <label className="text-sm font-bold text-slate-700">Identifiant</label>
                    <input 
                      type="text" 
                      required
                      className="w-full p-3 rounded-xl border border-slate-200 outline-none focus:border-primary transition-all"
                      value={loginForm.username}
                      onChange={(e) => setLoginForm({ ...loginForm, username: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-bold text-slate-700">Mot de passe</label>
                    <input 
                      type="password" 
                      required
                      className="w-full p-3 rounded-xl border border-slate-200 outline-none focus:border-primary transition-all"
                      value={loginForm.password}
                      onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                    />
                  </div>
                  <button type="submit" className="w-full bg-primary text-white py-3 rounded-xl font-bold mt-4 shadow-lg shadow-primary/20">
                    Se connecter
                  </button>
                </form>
              ) : (
                <form onSubmit={handleVerify2FA} className="space-y-4">
                  <div className="bg-primary/5 p-4 rounded-2xl border border-primary/10 mb-4">
                    <p className="text-xs text-slate-600 leading-relaxed">
                      Un code de vérification a été envoyé à l'adresse email de sécurité. Veuillez le saisir ci-dessous.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-bold text-slate-700">Code de vérification (6 chiffres)</label>
                    <input 
                      type="text" 
                      required
                      maxLength={6}
                      placeholder="000000"
                      className="w-full p-4 text-center text-2xl font-black tracking-[0.5em] rounded-xl border border-slate-200 outline-none focus:border-primary transition-all"
                      value={twoFactorCode}
                      onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, ''))}
                    />
                  </div>
                  <button type="submit" className="w-full bg-primary text-white py-3 rounded-xl font-bold mt-4 shadow-lg shadow-primary/20">
                    Vérifier le code
                  </button>
                  <button 
                    type="button" 
                    onClick={() => setShow2FA(false)}
                    className="w-full text-slate-600 text-xs font-bold hover:text-slate-800 transition-colors"
                  >
                    Retour à l'identification
                  </button>
                </form>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
    </MotionConfig>
    </LazyMotion>
  );
}


